import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import StudentVoices from "@/components/StudentVoices";
import UniversityImage from "@/components/UniversityImage";
import { Bookmark, Columns3, LineChart, Loader2, ExternalLink, Sparkles, Lock, Printer } from "lucide-react";
import { startGoogleLogin } from "@/lib/login";
import usePageMeta from "@/lib/usePageMeta";

export default function UniversityDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [u, setU] = useState(null);
  const [bookmarked, setBookmarked] = useState(false);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelError, setIntelError] = useState(null);
  const [myFit, setMyFit] = useState(null); // { fit_score, reasons[] } from /recommendations
  usePageMeta({
    title: u ? `${u.name}: cost in rupees, ranking and fit` : "University",
    description: u
      ? `${u.name}${u.city ? `, ${u.city}` : ""} (${u.country}): tuition and living costs in rupees, rankings, post-study work rights and fit for Indian students.`
      : undefined,
    path: `/universities/${slug}`,
  });

  const startGoogle = () => {
    startGoogleLogin("/dashboard");
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/universities/${slug}`);
      setU(data);
      if (user) {
        const s = await api.get("/shortlist");
        setBookmarked(!!s.data.find(x => x.slug === slug));
        // Fetch this user's personalised fit score for this uni
        try {
          const recs = await api.get("/recommendations");
          const mine = (recs.data?.results || []).find(r => r.slug === slug);
          if (mine) setMyFit({ fit_score: mine.fit_score, reasons: mine.reasons || [] });
        } catch { /* ignore */ }
      }
    })();
  }, [slug, user]);

  // Fetch AI-powered intelligence (auth-only)
  useEffect(() => {
    if (!user || !slug) return;
    setIntelLoading(true);
    setIntelError(null);
    api.post(`/intelligence/${slug}`)
      .then(({ data }) => setIntel(data))
      .catch((e) => setIntelError(e?.response?.data?.detail || "Could not generate intelligence"))
      .finally(() => setIntelLoading(false));
  }, [slug, user]);

  const toggleShort = async () => {
    if (!user) return nav("/");
    if (bookmarked) {
      await api.delete(`/shortlist/${slug}`);
      setBookmarked(false);
    } else {
      await api.post(`/shortlist/${slug}`);
      setBookmarked(true);
    }
  };

  if (!u) return <div className="min-h-screen flex items-center justify-center text-body-sm text-[var(--s400)]">Loading…</div>;
  const totalYr = u.tuition_per_year_inr + u.living_per_year_inr;

  return (
    <div className="min-h-screen bg-white lg:pl-[240px] print-root">
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
        <UniversityImage
          slug={u.slug}
          name={u.name}
          aspectClass="aspect-[21/9] md:aspect-[3/1]"
          rounded="rounded-[var(--r24)]"
          className="mb-8 shadow-sm border border-[var(--s200)]"
          showGradientOverlay
        />
        <div className="eyebrow mb-3">{u.country} · {u.city}</div>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <h1 className="font-semibold text-[var(--s900)] text-display md:text-display leading-none max-w-3xl">{u.name}</h1>
          <div className="flex gap-2 flex-wrap no-print">
            <button className="btn-ghost inline-flex items-center gap-2" onClick={toggleShort} data-testid="detail-bookmark">
              <Bookmark size={14} fill={bookmarked ? "currentColor" : "none"} strokeWidth={2} /> {bookmarked ? "Saved" : "Shortlist"}
            </button>
            <button className="btn-ghost inline-flex items-center gap-2" onClick={() => window.print()} data-testid="detail-print" title="Print or save as PDF">
              <Printer size={14} strokeWidth={2} /> Print
            </button>
            <button className="btn-ghost inline-flex items-center gap-2" onClick={() => nav(`/compare?slug=${u.slug}`)} data-testid="detail-compare">
              <Columns3 size={14} strokeWidth={2} /> Compare
            </button>
            <button className="btn-primary inline-flex items-center gap-2" onClick={() => nav(`/financial?slug=${u.slug}`)} data-testid="detail-financial">
              <LineChart size={14} strokeWidth={2} /> Financial plan
            </button>
          </div>
        </div>
        <p className="text-body-lg text-[var(--s600)] mt-5 max-w-3xl leading-relaxed">{u.summary}</p>

        {/* Key stats — tinted */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-5 mt-10">
          <Big k="QS World Rank" v={`#${u.qs_rank_2025}`} meta={u.field_meta?.qs_rank_2025} bg="#EEF2FF" />
          <Big k="IB Range" v={`${u.ib_score_range_low}–${u.ib_score_range_high}`} suffix="/45" meta={u.field_meta?.ib_score_range_low} bg="#ECFDF5" />
          <Big k="Total / year" v={fmtINR(totalYr)} meta={{ source: "Tuition + Living estimate", is_estimate: true }} bg="#F5F3FF" />
          <Big k="Employment" v={`${u.employment_rate_pct}%`} meta={u.field_meta?.employment_rate_pct} bg="#FFFBEB" />
          <Big
            k="Fit Score"
            v={myFit ? Math.round(myFit.fit_score) : (user ? "—" : "Sign in")}
            suffix={myFit ? "/100" : null}
            meta={{ source: myFit ? "Computed from your profile" : "Personalised", is_estimate: false }}
            bg="#F0F9FF"
          />
        </div>

        {/* Strengths / Trade-offs / Fit For You */}
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          <div className="card p-7" style={{ background: "#EEF2FF" }}>
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--ind)] mb-3">Strengths</div>
            <ul className="space-y-2.5 text-body text-[var(--s800)] leading-relaxed">
              {u.strengths?.map((s, i) => <li key={i}><span className="text-[var(--ind)] mr-2">—</span>{s}</li>)}
            </ul>
          </div>
          <div className="card p-7" style={{ background: "#FFFBEB" }}>
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--warning)] mb-3">Trade-offs</div>
            <ul className="space-y-2.5 text-body text-[var(--s800)] leading-relaxed">
              {u.tradeoffs?.map((s, i) => <li key={i}><span className="text-[var(--warning)] mr-2">—</span>{s}</li>)}
            </ul>
          </div>
          <div className="card p-7" style={{ background: "#F0F9FF" }} data-testid="detail-fit-for-you">
            <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[#0284C7] mb-3 flex items-center justify-between">
              <span>Fit For You</span>
              {myFit && (
                <span className="text-[#0369A1]">
                  <b className="text-h3 font-semibold leading-none">{Math.round(myFit.fit_score)}</b>
                  <span className="text-micro font-medium ml-0.5">/100</span>
                </span>
              )}
            </div>
            {!user ? (
              <p className="text-body text-[var(--s700)] leading-relaxed">
                <button onClick={startGoogle} className="text-[#0284C7] font-semibold underline underline-offset-2">Sign in</button> to see how this university matches your profile.
              </p>
            ) : myFit && myFit.reasons.length > 0 ? (
              <ul className="space-y-2.5 text-body text-[var(--s800)] leading-relaxed">
                {myFit.reasons.slice(0, 3).map((r, i) => (
                  <li key={i}><span className="text-[#0284C7] mr-2">—</span>{r}</li>
                ))}
              </ul>
            ) : (
              <p className="text-body text-[var(--s500)] leading-relaxed">
                Complete your profile to unlock a personalised fit explanation.
              </p>
            )}
          </div>
        </div>

        {/* Programs */}
        <div className="mt-10">
          <div className="eyebrow mb-3">Popular programs</div>
          <div className="flex flex-wrap gap-2">
            {u.popular_programs?.map(p => (
              <span key={p} className="text-body-sm px-3 py-1.5 rounded-[var(--r8)] bg-[var(--s100)] text-[var(--s700)] border border-[var(--s200)]">{p}</span>
            ))}
          </div>
        </div>

        {/* Admissions timeline + By the numbers (side by side on lg) */}
        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          {/* Timeline column */}
          {u.timeline?.milestones?.length > 0 && (
            <div className="lg:col-span-7">
              <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                  <div className="eyebrow mb-2">Admissions timeline</div>
                  <h2 className="text-h2 md:text-h2 font-semibold text-[var(--s900)] leading-tight">
                    {u.country} cycle, current year.
                  </h2>
                </div>
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] bg-[var(--s100)] border border-[var(--s200)] px-2.5 py-1.5 rounded-[var(--r8)]">
                  {u.timeline.cycle}
                </span>
              </div>
              <Timeline milestones={u.timeline.milestones} />
              <p className="text-label text-[var(--s400)] mt-5">
                Dates are typical for this country and may vary by programme. Always confirm on the university website.
              </p>
            </div>
          )}

          {/* By the numbers column */}
          <div className={u.timeline?.milestones?.length > 0 ? "lg:col-span-5" : "lg:col-span-12"}>
            <div className="eyebrow mb-3">By the numbers</div>
            <h2 className="text-h2 md:text-h2 font-semibold text-[var(--s900)] leading-tight mb-6">
              Verified facts &amp; estimates.
            </h2>
            <div className="card p-6 space-y-1">
              <Row k="Tuition / year" v={fmtINR(u.tuition_per_year_inr)}
                note={`~${u.tuition_native?.toLocaleString()} ${u.currency_native}`}
                meta={u.field_meta?.tuition_per_year_inr} />
              <Row k="Living / year" v={fmtINR(u.living_per_year_inr)} note="Varies by city" meta={u.field_meta?.living_per_year_inr} />
              <Row k="IB range" v={`${u.ib_score_range_low}–${u.ib_score_range_high} / 45`} note="Typical international undergrad offers" meta={u.field_meta?.ib_score_range_low} />
              <Row k="SAT range" v={`${u.sat_score_range_low}–${u.sat_score_range_high}`} note="Mid-50% admitted band" meta={u.field_meta?.sat_score_range_low} />
              <Row k="IELTS range" v={`${u.ielts_score_range_low}–${u.ielts_score_range_high}`} note="Overall, per programme" meta={u.field_meta?.ielts_score_range_low} />
              <Row k="Acceptance" v={`${u.acceptance_rate_pct}%`} meta={u.field_meta?.acceptance_rate_pct} />
              <Row k="International students" v={`${u.international_students_pct}%`} meta={u.field_meta?.international_students_pct} />
              <Row k="Post-study work" v={`${u.post_study_work_years} years`} note="Subject to visa policy" meta={u.field_meta?.post_study_work_years} />
              <Row last k="Scholarships" v={u.scholarships_available ? "Available" : "Limited"} note="Confirm on university website" meta={u.field_meta?.scholarships_available} />
            </div>
            <p className="text-label text-[var(--s400)] mt-4">
              Every figure shows its source. <span className="text-[var(--ind)] font-medium">est.</span> = estimate; verify with the institution.
            </p>
          </div>
        </div>

        {/* Scholarship Intelligence + Visa Intelligence */}
        <ScholarshipIntel u={u} user={user} intel={intel} loading={intelLoading} error={intelError} onSignIn={startGoogle} />
        <VisaIntel u={u} user={user} intel={intel} loading={intelLoading} error={intelError} onSignIn={startGoogle} />

        {/* Student Voices — community sentiment (unverified) */}
        <StudentVoices universityId={u.slug} />
      </div>
      <Footer />
    </div>
  );
}

function Big({ k, v, suffix, meta, bg }) {
  const src = meta?.source || "—";
  const est = meta?.is_estimate;
  return (
    <div className="card p-5" style={bg ? { background: bg } : undefined}>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-2 flex items-center gap-2">
        {k}
        {est && <span className="text-micro tracking-[0.05em] text-[var(--ind)] font-semibold" data-testid={`est-${k.replace(/\s+/g, "-").toLowerCase()}`}>· EST.</span>}
      </div>
      <div className="text-h1 font-semibold text-[var(--s900)]">
        {v}{suffix && <span className="text-body text-[var(--s500)] font-medium ml-1">{suffix}</span>}
      </div>
      <div className="text-micro text-[var(--s400)] mt-3">Source · {src}</div>
    </div>
  );
}

function Timeline({ milestones }) {
  return (
    <ol className="relative">
      {/* spine */}
      <span aria-hidden className="absolute left-[11px] top-2 bottom-2 w-px bg-[var(--s200)]" />
      {milestones.map((m, i) => {
        const isNext = m.status === "next";
        const isPast = m.status === "past";
        const dotCls = isNext
          ? "bg-[var(--ind)] border-[var(--ind)] shadow-[0_0_0_4px_rgba(79,70,229,0.12)]"
          : isPast
            ? "bg-white border-[var(--s300)]"
            : "bg-white border-[var(--ind)]";
        const date = formatDate(m.date);
        return (
          <li key={i} className="relative pl-9 pb-7 last:pb-0" data-testid={`milestone-${i}`}>
            <span className={`absolute left-0 top-1.5 w-[22px] h-[22px] rounded-full border-2 ${dotCls}`} />
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className={`font-mono text-label font-semibold tracking-wide ${isNext ? "text-[var(--ind)]" : isPast ? "text-[var(--s400)]" : "text-[var(--s700)]"}`}>
                {date}
              </span>
              {isNext && (
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-white bg-[var(--ind)] px-2 py-0.5 rounded">
                  Next
                </span>
              )}
              {isPast && (
                <span className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">
                  Passed
                </span>
              )}
            </div>
            <div className={`text-h3 font-semibold mt-1 leading-snug ${isPast ? "text-[var(--s500)]" : "text-[var(--s900)]"}`}>
              {m.title}
            </div>
            <p className={`text-body-sm mt-1 leading-relaxed ${isPast ? "text-[var(--s400)]" : "text-[var(--s600)]"} max-w-2xl`}>
              {m.description}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
function Row({ k, v, note, meta, last }) {
  const src = meta?.source;
  const est = meta?.is_estimate;
  return (
    <div className={`flex items-baseline justify-between gap-4 py-3 ${last ? "" : "border-b border-[var(--s200)]"}`}>
      <div className="min-w-0">
        <div className="text-body-sm text-[var(--s900)] font-medium flex items-center gap-2">
          {k}
          {est && <span className="text-micro tracking-[0.05em] text-[var(--ind)] uppercase font-semibold">est.</span>}
        </div>
        {(src || note) && (
          <div className="text-micro text-[var(--s400)] mt-1 leading-relaxed">
            {src ? <>Source · {src}</> : note}
            {src && note ? ` · ${note}` : ""}
          </div>
        )}
      </div>
      <div className="text-body font-semibold text-[var(--s900)] text-right whitespace-nowrap">{v}</div>
    </div>
  );
}

function competitionColor(c) {
  const v = (c || "").toLowerCase();
  if (v.includes("very")) return "bg-red-50 text-[var(--danger)] border-red-200";
  if (v.includes("high")) return "bg-orange-50 text-orange-700 border-orange-200";
  if (v.includes("medium")) return "bg-amber-50 text-[var(--warning)] border-amber-200";
  return "bg-emerald-50 text-[var(--success)] border-emerald-200";
}

/* Light tint background for the whole scholarship card based on competition level.
   High / Very-high = soft amber (rarer wins) · Medium = soft sky · Low = soft mint. */
function competitionCardBg(c) {
  const v = (c || "").toLowerCase();
  if (v.includes("very") || v.includes("high")) return "#FFFBEB"; // amber-50
  if (v.includes("medium")) return "#F0F9FF";                      // sky-50
  return "#ECFDF5";                                                // emerald-50
}

function ScholarshipIntel({ u, user, intel, loading, error, onSignIn }) {
  const list = intel?.scholarships || [];
  // AI-inference panel — uses dashed border + soft wash to read clearly distinct
  // from verified-data cards (same visual cue family as Student Voices).
  const panelStyle = {
    background: "linear-gradient(180deg,#FAFBFF 0%,#F4F6FF 100%)",
    border: "1px dashed #C7CFEA",
  };
  return (
    <section className="mb-12 rounded-[var(--r20)] p-7 lg:p-9" style={panelStyle} data-testid="scholarship-intel-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={13} className="text-[var(--ind)]" strokeWidth={2.2} />
        <span className="eyebrow text-[var(--ind)]">Scholarship Intelligence · AI-matched to your profile</span>
      </div>
      <h3 className="text-h2 md:text-h1 font-semibold text-[var(--s900)] leading-tight max-w-3xl">
        Scholarships you&apos;re actually eligible for — ranked by realistic odds.
      </h3>
      <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed max-w-2xl">
        Live matches at <span className="text-[var(--s800)] font-medium">{u.name}</span> and across {u.country}. Computed from your profile, the university&apos;s eligibility rules and historical award data.
      </p>

      <div className="mt-6">
        {!user ? (
          <SignInBlock onSignIn={onSignIn} title="Sign in to generate your personalised scholarship matches" />
        ) : loading ? (
          <div className="card p-10 flex items-center justify-center text-[var(--s500)] text-body-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Generating live scholarship matches…
          </div>
        ) : error ? (
          <div className="card p-6 text-body-sm text-[var(--danger)]">{error}</div>
        ) : list.length === 0 ? (
          <div className="card p-6 text-body-sm text-[var(--s500)]">No scholarship matches surfaced yet — check back after we expand the dataset.</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5">
            {list.map((s, i) => (
              <div
                key={i}
                className="card p-6 transition-all hover:-translate-y-0.5 hover:border-[var(--s300)]"
                style={{ background: competitionCardBg(s.competition) }}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-h3 font-semibold text-[var(--s900)] leading-tight">{s.name}</div>
                    <div className="text-micro text-[var(--s500)] mt-1 uppercase tracking-[0.1em]">{s.type}</div>
                  </div>
                  <span className={`shrink-0 text-micro font-semibold tracking-[0.05em] uppercase px-2 py-1 rounded-[var(--r8)] border ${competitionColor(s.competition)}`}>
                    {s.competition}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[var(--s200)]">
                  <Stat k="Match" v={`${s.eligibility_match}%`} accent="ind" />
                  <Stat k="Success" v={`${s.success_probability}%`} accent="emerald" />
                  <Stat k="Value" v={s.value} compact />
                </div>

                <div className="mt-4 text-body-sm leading-relaxed text-[var(--s700)]">
                  <span className="text-[var(--s500)] font-semibold">When:</span> {s.deadline}
                </div>
                <div className="mt-1.5 text-body-sm leading-relaxed text-[var(--s700)]">
                  <span className="text-[var(--s500)] font-semibold">What to expect:</span> {s.what_to_expect}
                </div>
                {s.apply_at && s.apply_at.startsWith("http") && (
                  <a
                    href={s.apply_at}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-4 text-body-sm font-semibold text-[var(--ind)] hover:underline"
                  >
                    Apply <ExternalLink size={12} strokeWidth={2.2} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function VisaIntel({ u, user, intel, loading, error, onSignIn }) {
  const v = intel?.visa;
  // AI-inference panel — same dashed-border family as Scholarship & Student Voices.
  const panelStyle = {
    background: "linear-gradient(180deg,#FBFAFF 0%,#F6F2FF 100%)",
    border: "1px dashed #D2C9F0",
  };
  return (
    <section className="mb-12 rounded-[var(--r20)] p-7 lg:p-9" style={panelStyle} data-testid="visa-intel-section">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={13} className="text-[var(--ind)]" strokeWidth={2.2} />
        <span className="eyebrow text-[var(--ind)]">Visa Intelligence · {u.country}</span>
      </div>
      <h3 className="text-h2 md:text-h1 font-semibold text-[var(--s900)] leading-tight max-w-3xl">
        Decision support for the journey, not just the offer.
      </h3>
      <p className="text-body-sm text-[var(--s600)] mt-2 leading-relaxed max-w-2xl">
        Reflects current {u.country} policy for Indian undergraduate applicants — pathway, post-study work, PR realism and processing timelines.
      </p>

      <div className="mt-6">
        {!user ? (
          <SignInBlock onSignIn={onSignIn} title="Sign in to generate your personalised visa briefing" />
        ) : loading ? (
          <div className="card p-10 flex items-center justify-center text-[var(--s500)] text-body-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Generating visa briefing…
          </div>
        ) : error ? (
          <div className="card p-6 text-body-sm text-[var(--danger)]">{error}</div>
        ) : !v ? (
          <div className="card p-6 text-body-sm text-[var(--s500)]">Visa briefing unavailable.</div>
        ) : (
          <div className="card p-7">
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-5">
              <VisaRow k="Visa pathway" v={v.pathway} />
              <VisaRow k="Post-study work rights" v={v.post_study_work} accent="emerald" />
              <VisaRow k="PR opportunities" v={v.pr_opportunities} />
              <VisaRow k="Processing timeline" v={v.processing_timeline} />
              <VisaRow k="Estimated costs" v={v.estimated_costs} />
              <VisaRow k="Country policy updates" v={v.policy_notes} />
            </div>
            {v.required_documents?.length > 0 && (
              <div className="mt-6 pt-5 border-t border-[var(--s200)]">
                <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-3">Required documents</div>
                <div className="flex flex-wrap gap-2">
                  {v.required_documents.map((d, i) => (
                    <span key={i} className="text-label px-3 py-1.5 rounded-[var(--r8)] border border-[var(--s200)] bg-white text-[var(--s700)]">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ k, v, accent, compact }) {
  const cls = accent === "ind" ? "text-[var(--ind)]" : accent === "emerald" ? "text-[var(--success)]" : "text-[var(--s900)]";
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s400)]">{k}</div>
      <div className={`${compact ? "text-body-sm" : "text-h3"} font-semibold ${cls} mt-1 leading-tight`}>{v}</div>
    </div>
  );
}

function VisaRow({ k, v, accent }) {
  const valCls = accent === "emerald" ? "text-[var(--success)]" : "text-[var(--s800)]";
  return (
    <div>
      <div className="text-micro font-semibold tracking-[0.05em] uppercase text-[var(--s500)] mb-1">{k}</div>
      <div className={`text-body-sm leading-relaxed ${valCls}`}>{v}</div>
    </div>
  );
}

function SignInBlock({ onSignIn, title }) {
  return (
    <div className="card p-8 text-center">
      <div className="w-11 h-11 mx-auto rounded-full bg-[var(--ind-soft)] text-[var(--ind)] flex items-center justify-center mb-4">
        <Lock size={18} strokeWidth={1.8} />
      </div>
      <h4 className="text-h3 font-semibold text-[var(--s900)]">{title}</h4>
      <p className="text-body-sm text-[var(--s600)] mt-1.5 leading-relaxed max-w-md mx-auto">
        We use your profile (academics, budget, language, timeline) to compute realistic odds and a country-accurate visa briefing.
      </p>
      <button onClick={onSignIn} className="btn-primary mt-5 inline-flex items-center justify-center gap-2">Start free</button>
    </div>
  );
}
